import { Button, IconButton } from "@material-ui/core";
import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import SupportEditModal from "./supportEditModal";
import Tooltip from "@material-ui/core/Tooltip";
import Visibility from "@material-ui/icons/Visibility";
import Close from "@material-ui/icons/Close";

// TruncatedText component for handling text truncation with hover
const TruncatedText = ({ text, maxLength = 20, children }) => {
  const displayText = children || text;
  
  if (!displayText || displayText.length <= maxLength) {
    return <span>{displayText}</span>;
  }

  return (
    <Tooltip title={displayText} placement="top">
      <span style={{ cursor: 'help' }}>
        {displayText.substring(0, maxLength)}...
      </span>
    </Tooltip>
  );
};

const SupportTable = ({
  user,
  records,
  handleChangePage,
  rowsPerPage,
  rowCount,
  setPop,
  handleDelete,
  ticketAssignedToOption,
}) => {
  const { REACT_APP_SERVER_URL } = process.env;
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editTicketId, setEditTicketId] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");
  const [isPreviewVideo, setIsPreviewVideo] = useState(false);
  const [isPreviewImage, setIsPreviewImage] = useState(false);

  const handlePreview = (e, url, title, isVideo = false, isImage = false) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setPreviewUrl(url);
    setPreviewTitle(title);
    setIsPreviewVideo(isVideo);
    setIsPreviewImage(isImage);
    setPreviewOpen(true);
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewUrl("");
    setPreviewTitle("");
    setIsPreviewVideo(false);
    setIsPreviewImage(false);
  };

  const openEditModal = (row) => {
    setEditTicketId(row._id);
    setEditModalOpen(true);
  };
  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditTicketId(null);
  };
  const handleEditSaved = () => {
    closeEditModal();
  };

  const attachmentCell = (row) => {
    return (
      <span>
        {row.attachments && row.attachments.length
          ? row.attachments.map((v) => {
              const url = `https://${REACT_APP_SERVER_URL}/downloads/downloadAWSFile?fid=${row._id}&fileName=${v.fileName}&endpoint=supportTickets`;
              const previewUrl = `${url}&preview=true`;
              const isVideo = v.fileName && (v.fileName.toLowerCase().endsWith('.mp4') || v.fileName.toLowerCase().endsWith('.webm') || v.fileName.toLowerCase().endsWith('.ogg'));
              const isImage = v.fileName && (v.fileName.toLowerCase().endsWith('.jpg') || v.fileName.toLowerCase().endsWith('.jpeg') || v.fileName.toLowerCase().endsWith('.png') || v.fileName.toLowerCase().endsWith('.gif') || v.fileName.toLowerCase().endsWith('.webp'));
              
              return (
                <div
                  key={v.fileName}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "5px",
                    gap: "8px"
                  }}
                >
                  <Tooltip title="Preview Attachment">
                    <IconButton 
                      size="small" 
                      onClick={(e) => handlePreview(e, previewUrl, v.fileName, isVideo, isImage)}
                      style={{ padding: 4 }}
                    >
                      <Visibility fontSize="small" color="primary" />
                    </IconButton>
                  </Tooltip>
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      maxWidth: "120px",
                      fontSize: "0.85rem",
                      color: "#3f51b5"
                    }}
                  >
                    {v.fileName}
                  </a>
                </div>
              );
            })
          : null}
      </span>
    );
  };

  const columnsadmin = [
    { name: "status", cell: (row) => <span>{row.status}</span>, width: "100px" },
    { name: "Created Date", cell: (row) => <span>{window.moment(row.createdOn).format("DD-MMM-YYYY hh:mm")}</span>, width: "100px" },
    {
      name: "Schedule Time",
      cell: (row) => (
        <span>
          {row.status === "Resolved" ? "Ticket Closed" : row.scheduledTime ? window.moment(row.scheduledTime).format('DD-MMM-YYYY hh:mm') : (
            <Button color="secondary" size="sm" onClick={(e) => setPop(true)}>Schedule</Button>
          )}
        </span>
      ),
    },
    { name: "Product", cell: (row) => <span>{row.product}</span>, width: "100px" },
    { name: "Plan", cell: (row) => <span>{row.plan}</span>, width: "100px" },
    { name: "Category", cell: (row) => <span>{row.category}</span>, width: "100px" },
    { name: "Concern", width: "200px", cell: (row) => <TruncatedText>{row.subCategory}</TruncatedText> },
    {
      name: "Comment",
      width: "300px",
      cell: (row) => (
        <span>
          <TruncatedText>{row.comment}</TruncatedText>{" "}
          {user.is_superuser && (
            <Button color="primary" size="small" onClick={() => openEditModal(row)}>Edit</Button>
          )}
        </span>
      ),
    },
    { name: "Admin Comment", cell: (row) => <TruncatedText>{row.adminComment}</TruncatedText>, width: "200px" },
    { name: "Company", cell: (row) => <TruncatedText>{row.company}</TruncatedText>, width: "100px" },
    { name: "Phone", cell: (row) => <span>{row.phone}</span>, width: "100px" },
    {
      name: "Subscriber ID",
      cell: (row) => (
        <span>
          {row.externalQuery ? (
            <p style={{ lineBreak: "anywhere" }}> 
              <TruncatedText>{row.externalAdmin}</TruncatedText>
            </p>
          ) : (
            <TruncatedText>{row.admin}</TruncatedText>
          )}
        </span>
      ),
      width: "200px",
    },
    { name: "Assign To", selector: "assignTo", width: "200px" },
    { name: "Attachments", cell: attachmentCell, width: "200px" },
    { name: "Closed On", cell: (row) => <span>{row.resolvedOn}</span>, width: "100px" },
    { name: "Development Status", selector: "", width: "100px", cell: (row) => <>{row.developmentStatus}</> },
    {
      name: "Delete",
      width: "100px",
      cell: (row) => (
        <Button color="secondary" size="small" onClick={() => handleDelete(row._id)}>Delete</Button>
      ),
    },
    { name: "Feedback", cell: (row) => <span>{row.feedbackOption} - {row.feedbackRemarks}</span>, width: "100px" }
  ];

  const columns = [
    { name: "Status", cell: (row) => <span>{row.status}</span>, width: "180px" },
    { name: "Last Update Date", cell: (row) => <span>{window.moment(row.updatedOn).format("DD-MMM-YYYY hh:mm")}</span>, width: "100px" },
    {
      name: "Schedule Time",
      cell: (row) => (
        <span>
          {row.status === "Resolved" ? "Ticket Closed" : row.scheduledTime ? window.moment(row.scheduledTime).format('DD-MMM-YYYY hh:mm') : (
            <Button color="secondary" size="sm" onClick={(e) => setPop(true)}>Schedule</Button>
          )}
        </span>
      ),
    },
    { name: "Updated By", cell: (row) => <span>{row.updatedBy}</span>, width: "100px" },
    { name: "Category", cell: (row) => <span>{row.category}</span>, width: "100px" },
    { name: "Concern", cell: (row) => <TruncatedText>{row.subCategory}</TruncatedText>, width: "300px" },
    {
      name: "Comment",
      width: "300px",
      cell: (row) => (
        <span>
          <TruncatedText>{row.comment}</TruncatedText>
          {user.is_superuser && (
            <>
              <a style={{ color: "blue" }} href={"/admin/editsupport/" + row._id}><b> edit </b></a>
              <Button color="secondary" size="small" onClick={() => handleDelete(row._id)}>Delete</Button>
            </>
          )}
        </span>
      ),
    },
    { name: "Betyphon team", cell: (row) => <span>{row.adminComment}</span>, width: "200px" },
    { name: "Assign To", selector: "assignTo", width: "200px" },
    { name: "Attachments", cell: attachmentCell, width: "200px" },
    { name: "Closed On", cell: (row) => <span>{row.resolvedOn}</span>, width: "100px" },
    { name: "Feedback", cell: (row) => <span>{row.feedbackOption} - {row.feedbackRemarks}</span>, width: "100px" }
  ];

  return (
    <>
      <Dialog
        open={previewOpen}
        onClose={closePreview}
        fullWidth={true}
        maxWidth="lg"
        disableEnforceFocus
        disableAutoFocus
        style={{ zIndex: 9999 }}
        PaperProps={{
          style: {
            zIndex: 10000,
          },
        }}
      >
        <DialogTitle>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>{previewTitle}</span>
            <IconButton aria-label="close" onClick={closePreview}>
              <Close />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent dividers>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
            {isPreviewImage ? (
              <img src={previewUrl} alt={previewTitle} style={{ maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
            ) : isPreviewVideo ? (
              <video src={previewUrl} controls style={{ maxWidth: '100%', maxHeight: '75vh', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                Your browser does not support the video tag.
              </video>
            ) : (
              <iframe
                src={previewUrl}
                title="Attachment Preview"
                style={{ width: "100%", height: "75vh", border: "none", backgroundColor: '#fff' }}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      <DataTable
        columns={user.is_superuser ? columnsadmin : columns}
        data={records || []}
        highlightOnHover
        pagination
        onChangePage={handleChangePage}
        paginationPerPage={rowsPerPage}
        paginationServer
        paginationTotalRows={rowCount}
        paginationComponentOptions={{
          noRowsPerPage: true,
        }}
      />

      <SupportEditModal
        open={editModalOpen}
        onClose={closeEditModal}
        ticketId={editTicketId}
        user={user}
        onSaved={handleEditSaved}
        ticketAssignedToOption={ticketAssignedToOption}
      />
    </>
  );
};

export default SupportTable;
